import { useMemo, useState } from 'react'
import { useRooms } from '../api/rooms'
import {
  exportChecklist,
  useBuildFromTemplate,
  useChecklistOverlay,
  useChecklistTemplate,
  useChecklistTemplates,
  useCopyChecklistFromRoom,
  useImportChecklist,
  useSaveChecklistAsTemplate,
  type ChecklistMergeResponse,
  type ChecklistTemplate,
  type TemplateParameters,
} from '../api/templates'
import { errorMessage } from '../lib/format'
import {
  filledParameters,
  mergeSummary,
  parameterFields,
  parameterProblem,
  type ParameterField,
} from '../lib/templates'

/**
 * How a list gets into a room other than by typing it (ADR 0010): from a stored template with the
 * deal's particulars filled in once, from an overlay merged after a preview, from another room the
 * caller owns, or from a spreadsheet. Nothing here decides anything: the API refuses what is not
 * allowed, and a preview is the API's own answer with the writing left out.
 */
type Path = 'template' | 'copy' | 'import'

const DEAL_TYPES = [
  ['MnA', 'M&A'],
  ['CapitalRaise', 'Capital raise'],
  ['DebtFinancing', 'Debt financing'],
  ['Custom', 'Custom'],
] as const

export function StartAList({
  roomId,
  organizationId,
  livingSectionCodes,
  onDone,
}: {
  roomId: string
  organizationId: string
  livingSectionCodes: readonly string[]
  onDone: () => void
}) {
  const [path, setPath] = useState<Path>('template')
  return (
    <div className="form form-wide" role="group" aria-label="Templates, copy or import">
      <div className="chips" role="radiogroup" aria-label="Where the list comes from">
        {(
          [
            ['template', 'A template'],
            ['copy', 'Another room'],
            ['import', 'A spreadsheet'],
          ] as const
        ).map(([value, label]) => (
          <label
            key={value}
            className={path === value ? 'chip chip-pick chip-picked' : 'chip chip-pick'}
          >
            <input
              type="radio"
              name="list-source"
              checked={path === value}
              onChange={() => setPath(value)}
            />
            {label}
          </label>
        ))}
      </div>
      {path === 'template' && (
        <FromTemplate
          roomId={roomId}
          organizationId={organizationId}
          livingSectionCodes={livingSectionCodes}
          onDone={onDone}
        />
      )}
      {path === 'copy' && (
        <CopyFromRoom roomId={roomId} organizationId={organizationId} onDone={onDone} />
      )}
      {path === 'import' && <ImportList roomId={roomId} onDone={onDone} />}
    </div>
  )
}

// ------------------------------------------------------------------ a template or an overlay

function FromTemplate({
  roomId,
  organizationId,
  livingSectionCodes,
  onDone,
}: {
  roomId: string
  organizationId: string
  livingSectionCodes: readonly string[]
  onDone: () => void
}) {
  const templates = useChecklistTemplates(organizationId)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const template = useChecklistTemplate(organizationId, templateId)

  if (templates.isPending) return <p className="muted small">Loading templates…</p>
  if (templates.isError) {
    return (
      <>
        <p className="small muted no-margin">
          Stored templates are not available right now. “Start from the Master DRL” still works.
        </p>
        <Cancel onDone={onDone} />
      </>
    )
  }

  return (
    <>
      <label className="field">
        <span>Template</span>
        <select value={templateId ?? ''} onChange={(e) => setTemplateId(e.target.value || null)}>
          <option value="">Choose a template…</option>
          {templates.data.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.itemCount} requests{t.isOverlay ? ', overlay' : ''}
              {t.isSystem ? '' : ', yours'})
            </option>
          ))}
        </select>
      </label>
      {templateId === null && <Cancel onDone={onDone} />}
      {templateId !== null && template.isPending && <p className="muted small">Loading…</p>}
      {templateId !== null && template.isError && (
        <p className="error-text small">{errorMessage(template.error)}</p>
      )}
      {template.data &&
        (template.data.isOverlay ? (
          <ApplyOverlay
            key={template.data.id}
            roomId={roomId}
            template={template.data}
            onDone={onDone}
          />
        ) : (
          <BuildFromTemplate
            key={template.data.id}
            roomId={roomId}
            template={template.data}
            livingSectionCodes={livingSectionCodes}
            onDone={onDone}
          />
        ))}
    </>
  )
}

function ParameterInputs({
  fields,
  values,
  onChange,
  showProblems,
}: {
  fields: readonly ParameterField[]
  values: TemplateParameters
  onChange: (next: TemplateParameters) => void
  showProblems: boolean
}) {
  if (fields.length === 0) return null
  return (
    <fieldset className="checklist-picker">
      <legend className="small">This deal’s particulars, written into the requests</legend>
      {fields.map((f) => {
        const problem = showProblems ? parameterProblem(f, values[f.token]) : null
        return (
          <label key={f.token} className="field">
            <span>{f.label}</span>
            <input
              type={f.dataType === 'Date' ? 'date' : 'text'}
              inputMode={f.dataType === 'Year' ? 'numeric' : undefined}
              maxLength={200}
              value={values[f.token] ?? ''}
              onChange={(e) => onChange({ ...values, [f.token]: e.target.value })}
            />
            {problem && <span className="field-error">{problem}</span>}
          </label>
        )
      })}
    </fieldset>
  )
}

function BuildFromTemplate({
  roomId,
  template,
  livingSectionCodes,
  onDone,
}: {
  roomId: string
  template: ChecklistTemplate
  livingSectionCodes: readonly string[]
  onDone: () => void
}) {
  const build = useBuildFromTemplate(roomId)
  // A workstream that already lives in the room cannot be built again: it is left unticked and says why.
  const living = (code: string) =>
    livingSectionCodes.some((c) => c.toLowerCase() === code.toLowerCase())
  const [codes, setCodes] = useState<string[]>(
    template.sections.filter((s) => !living(s.code) && s.items.length > 0).map((s) => s.code),
  )
  const [values, setValues] = useState<TemplateParameters>({})
  const [tried, setTried] = useState(false)
  const fields = useMemo(() => parameterFields(template, codes), [template, codes])
  const total = template.sections
    .filter((s) => codes.includes(s.code))
    .reduce((n, s) => n + s.items.length, 0)
  const invalid = fields.some((f) => parameterProblem(f, values[f.token]) !== null)

  return (
    <form
      className="stack-sm"
      aria-label={`Build from ${template.name}`}
      onSubmit={(e) => {
        e.preventDefault()
        setTried(true)
        if (invalid) return
        build.mutate(
          {
            templateId: template.id,
            sectionCodes: codes.length === template.sections.length ? null : codes,
            parameters: filledParameters(fields, values),
          },
          { onSuccess: onDone },
        )
      }}
    >
      {template.description && <p className="small muted no-margin">{template.description}</p>}
      <fieldset className="checklist-picker">
        <legend className="small">Workstreams to include</legend>
        <div className="chips">
          {template.sections.map((s) => {
            const picked = codes.includes(s.code)
            const blocked = living(s.code)
            return (
              <label
                key={s.id}
                className={picked ? 'chip chip-pick chip-picked' : 'chip chip-pick'}
                title={blocked ? 'Already in this room' : undefined}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  disabled={blocked}
                  onChange={() =>
                    setCodes(picked ? codes.filter((c) => c !== s.code) : [...codes, s.code])
                  }
                />
                {s.name} ({s.items.length}){blocked ? ', already here' : ''}
              </label>
            )
          })}
        </div>
      </fieldset>
      <ParameterInputs fields={fields} values={values} onChange={setValues} showProblems={tried} />
      {build.error && <p className="error-text small">{errorMessage(build.error)}</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={build.isPending || total === 0}>
          {build.isPending ? 'Building…' : `Add ${total} requests`}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function ApplyOverlay({
  roomId,
  template,
  onDone,
}: {
  roomId: string
  template: ChecklistTemplate
  onDone: () => void
}) {
  const overlay = useChecklistOverlay(roomId)
  const [values, setValues] = useState<TemplateParameters>({})
  const [tried, setTried] = useState(false)
  const [preview, setPreview] = useState<ChecklistMergeResponse | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  const fields = useMemo(() => parameterFields(template, null), [template])
  const invalid = fields.some((f) => parameterProblem(f, values[f.token]) !== null)
  const parameters = filledParameters(fields, values)

  return (
    <form
      className="stack-sm"
      aria-label={`Apply ${template.name}`}
      onSubmit={(e) => {
        e.preventDefault()
        setTried(true)
        if (invalid) return
        if (preview === null) {
          overlay.mutate(
            { commit: false, templateId: template.id, parameters, itemIds: null },
            {
              onSuccess: (result) => {
                setPreview(result)
                // What the list already asks is left unticked; the owner may tick it all the same.
                setPicked(
                  result.rows.flatMap((r) =>
                    r.templateItemId && !r.alreadyThere ? [r.templateItemId] : [],
                  ),
                )
              },
            },
          )
        } else {
          overlay.mutate(
            { commit: true, templateId: template.id, parameters, itemIds: picked },
            { onSuccess: onDone },
          )
        }
      }}
    >
      <p className="small muted no-margin">
        An overlay adds its requests to a workstream of this list. Nothing is written until you have
        seen what it would add.
      </p>
      {preview === null && (
        <ParameterInputs
          fields={fields}
          values={values}
          onChange={setValues}
          showProblems={tried}
        />
      )}
      {preview !== null && (
        <table className="table" aria-label="What the overlay would add">
          <thead>
            <tr>
              <th scope="col">Add</th>
              <th scope="col">Workstream</th>
              <th scope="col">Request</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((r, n) => (
              <tr key={r.templateItemId ?? n}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Add ${r.title}`}
                    disabled={r.templateItemId === null || r.action === 'error'}
                    checked={r.templateItemId !== null && picked.includes(r.templateItemId)}
                    onChange={() => {
                      const id = r.templateItemId
                      if (id === null) return
                      setPicked(
                        picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id],
                      )
                    }}
                  />
                </td>
                <td>{r.code}</td>
                <td>{r.title}</td>
                <td className="small muted">
                  {r.action === 'error'
                    ? (r.message ?? 'Cannot be added')
                    : r.alreadyThere
                      ? 'The list already asks exactly this'
                      : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {overlay.error && <p className="error-text small">{errorMessage(overlay.error)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={overlay.isPending || (preview !== null && picked.length === 0)}
        >
          {overlay.isPending
            ? 'Working…'
            : preview === null
              ? 'Preview'
              : `Add ${picked.length} request${picked.length === 1 ? '' : 's'}`}
        </button>
        {preview !== null && (
          <button type="button" className="btn" onClick={() => setPreview(null)}>
            Back
          </button>
        )}
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

// ------------------------------------------------------------------ another room

function CopyFromRoom({
  roomId,
  organizationId,
  onDone,
}: {
  roomId: string
  organizationId: string
  onDone: () => void
}) {
  const rooms = useRooms()
  const copy = useCopyChecklistFromRoom(roomId)
  const [source, setSource] = useState('')
  // The API's rule, drawn: a room of this organization that the caller owns. It decides again on the click.
  const candidates = (rooms.data ?? []).filter(
    (r) => r.organizationId === organizationId && r.memberRole === 'Owner' && r.id !== roomId,
  )

  return (
    <form
      className="stack-sm"
      aria-label="Copy from another room"
      onSubmit={(e) => {
        e.preventDefault()
        if (source) copy.mutate(source, { onSuccess: onDone })
      }}
    >
      <p className="small muted no-margin">
        Copies the questions of another room you own in this organization: its workstreams and
        requests. Never its answers, states, notes, dates, assignees or documents.
      </p>
      {candidates.length === 0 ? (
        <p className="small muted no-margin">You own no other room in this organization.</p>
      ) : (
        <label className="field">
          <span>Room</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose a room…</option>
            {candidates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {copy.error && <p className="error-text small">{errorMessage(copy.error)}</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={copy.isPending || !source}>
          {copy.isPending ? 'Copying…' : 'Copy the questions'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

// ------------------------------------------------------------------ a spreadsheet

function ImportList({ roomId, onDone }: { roomId: string; onDone: () => void }) {
  const run = useImportChecklist(roomId)
  const [file, setFile] = useState<File | null>(null)
  const [strategy, setStrategy] = useState<'add' | 'replace'>('add')
  const [preview, setPreview] = useState<ChecklistMergeResponse | null>(null)
  const reset = () => {
    setPreview(null)
    run.reset()
  }
  const changes = preview ? preview.added + preview.updated + preview.removed : 0

  return (
    <form
      className="stack-sm"
      aria-label="Import a spreadsheet"
      onSubmit={(e) => {
        e.preventDefault()
        if (!file) return
        if (preview === null)
          run.mutate({ file, commit: false, strategy }, { onSuccess: setPreview })
        else run.mutate({ file, commit: true, strategy }, { onSuccess: onDone })
      }}
    >
      <p className="small muted no-margin">
        A CSV or Excel file with the columns of this list’s export. Requests are matched by their
        document label. Only the questions are read: status, notes, dates and assignees in the file
        are ignored, so a spreadsheet cannot sign a request off.
      </p>
      <label className="field">
        <span>File (.csv or .xlsx, up to 5 MB and 2,000 rows)</span>
        <input
          type="file"
          accept=".csv,.xlsx"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null)
            reset()
          }}
        />
      </label>
      <label className="field">
        <span>Requests of these workstreams that the file does not name</span>
        <select
          value={strategy}
          onChange={(e) => {
            setStrategy(e.target.value === 'replace' ? 'replace' : 'add')
            reset()
          }}
        >
          <option value="add">Keep them</option>
          <option value="replace">Remove them: the file is the list</option>
        </select>
      </label>
      {preview !== null && <MergePreview result={preview} />}
      {run.error && <p className="error-text small">{errorMessage(run.error)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={
            run.isPending || !file || (preview !== null && (preview.errors > 0 || changes === 0))
          }
        >
          {run.isPending ? 'Working…' : preview === null ? 'Preview' : 'Import'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

const ACTION_LABEL: Record<string, string> = {
  add: 'Add',
  update: 'Update',
  unchanged: 'No change',
  remove: 'Remove',
  skip: 'Skip',
  error: 'Error',
}

/** What the API said it would do, row by row. Unchanged rows are counted, not listed: there may be thousands. */
export function MergePreview({ result }: { result: ChecklistMergeResponse }) {
  const shown = result.rows.filter((r) => r.action !== 'unchanged')
  return (
    <div className="stack-sm">
      <p className="no-margin" role="status">
        {mergeSummary(result)}
        {result.errors > 0 && '. Nothing can be imported until every row is right.'}
      </p>
      {shown.length > 0 && (
        <table className="table" aria-label="What the import would do">
          <thead>
            <tr>
              <th scope="col">Row</th>
              <th scope="col">Workstream</th>
              <th scope="col">Label</th>
              <th scope="col">Request</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 200).map((r, n) => (
              <tr key={n} className={r.action === 'error' ? 'row-error' : undefined}>
                <td className="small muted">
                  {r.line === null
                    ? ''
                    : r.sheet && r.sheet !== 'csv'
                      ? `${r.sheet}, ${r.line}`
                      : r.line}
                </td>
                <td>{r.code}</td>
                <td>{r.docLabel ?? ''}</td>
                <td>{r.title}</td>
                <td>
                  {ACTION_LABEL[r.action] ?? r.action}
                  {r.message && <span className="small muted">: {r.message}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {shown.length > 200 && (
        <p className="small muted no-margin">And {shown.length - 200} more rows.</p>
      )}
    </div>
  )
}

function Cancel({ onDone }: { onDone: () => void }) {
  return (
    <div className="form-actions">
      <button type="button" className="btn" onClick={onDone}>
        Cancel
      </button>
    </div>
  )
}

// ------------------------------------------------------------------ out

/** The list as a file, through the API (bearer token, audit row). Anyone who may read the list may export what they read. */
export function ExportChecklist({ roomId }: { roomId: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = (format: 'csv' | 'xlsx') => {
    setBusy(true)
    setError(null)
    exportChecklist(roomId, format)
      .catch((e: unknown) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  return (
    <>
      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => run('xlsx')}>
        Export Excel
      </button>
      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => run('csv')}>
        Export CSV
      </button>
      {error && <span className="error-text small">{error}</span>}
    </>
  )
}

export function SaveAsTemplate({
  roomId,
  organizationId,
  onDone,
}: {
  roomId: string
  organizationId: string
  onDone: () => void
}) {
  const templates = useChecklistTemplates(organizationId, { includeInactive: true })
  const save = useSaveChecklistAsTemplate(roomId, organizationId)
  const [name, setName] = useState('')
  const [dealType, setDealType] = useState('Custom')
  const [industryTag, setIndustryTag] = useState('')
  const [replace, setReplace] = useState('')
  const own = (templates.data ?? []).filter((t) => !t.isSystem && !t.isOverlay)

  if (save.data) {
    return (
      <div className="form form-wide" role="status">
        <p className="no-margin">
          {save.data.replaced ? 'Replaced' : 'Saved'} “{save.data.name}”: {save.data.sectionCount}{' '}
          workstreams, {save.data.itemCount} requests.
          {save.data.tokens.length > 0 &&
            ` It asks for ${save.data.tokens.join(', ')} when a list is built from it; describe them on the organization page.`}
        </p>
        <Cancel onDone={onDone} />
      </div>
    )
  }

  return (
    <form
      className="form form-wide"
      aria-label="Save as a template"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({
          name: name.trim(),
          description: null,
          dealType,
          industryTag: industryTag.trim() || null,
          replaceTemplateId: replace || null,
        })
      }}
    >
      <p className="small muted no-margin">
        Saves this list’s workstreams and requests, never its answers, as a template of your
        organization. Write {'{{Target}}'} or any {'{{Name}}'} in a request and whoever builds from
        the template is asked for it. Items outside a section are left out. Organization owners
        only.
      </p>
      <label className="field">
        <span>Replace</span>
        <select
          value={replace}
          onChange={(e) => {
            setReplace(e.target.value)
            const chosen = own.find((t) => t.id === e.target.value)
            if (chosen) {
              setName(chosen.name)
              setDealType(chosen.dealType)
              setIndustryTag(chosen.industryTag ?? '')
            }
          }}
        >
          <option value="">Nothing: a new template</option>
          {own.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Name</span>
        <input value={name} maxLength={200} required onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>Deal type</span>
        <select value={dealType} onChange={(e) => setDealType(e.target.value)}>
          {DEAL_TYPES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Industry (optional)</span>
        <input
          value={industryTag}
          maxLength={100}
          onChange={(e) => setIndustryTag(e.target.value)}
        />
      </label>
      {save.error && <p className="error-text small">{errorMessage(save.error)}</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : replace ? 'Replace the template' : 'Save the template'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
